import {
  FetchApiError,
  getFormURLEncodedBody,
  handleFetchResponse,
} from '@stripe-internal/connect-embedded-lib';
import STRIPE_API_VERSION from '@stripe-internal/data-v1/schema/version';
import type {IDeferredPromise} from '../utils/DeferredPromise';
import {DeferredPromise} from '../utils/DeferredPromise';
import type {
  IFrameClaimAccountSessionRequest,
  IFrameClaimAccountSessionResponse,
  IFrameMessageFailedResponse,
  IFrameMessageFetchErrorResponse,
} from '../data-layer-client/FrameMessenger';
import type {AccountSessionClaim as FilteredAccountSessionClaim} from './types';
import {
  setObservabilityAuthMetadata,
  setDataLayerFrameLivemode,
  setClaimInfo,
} from './DataLayer';
import {authStore} from './authentication/AuthenticationStore';
import {getDevLogger} from '../utils/getLogger';
import {
  ACCOUNT_HEADER,
  CONTEXT_HEADER,
  LIVEMODE_HEADER,
  STRIPE_VERSION_HEADER,
} from '../utils/headerConstants';
import type {AccountSessionClaimFragmentData} from '../data-layer-client/accountSessionClaimFragment';
import {serializeFetchErrorForPostMessage} from '../data-layer-client/utils';
import type {USE_FOR_ANALYTICS_ONLY_HostApp} from '../connect/ConnectJSInterface/InitAndUpdateOptionsTypes';

const devLogger = getDevLogger();

// Initial platform authentication request that must complete before any other requests
let claimAccountSessionPromise: IDeferredPromise<AccountSessionClaimFragmentData> =
  new DeferredPromise();

/* eslint-disable camelcase */
const extractObservabilityAuthMetadata = (
  claimResponse: AccountSessionClaimFragmentData,
) => {
  const {
    account_id,
    platform_id,
    livemode,
    is_v2_account_session,
    merchant_data,
  } = claimResponse;

  const {
    country,
    has_express_dashboard_access,
    has_standard_dashboard_access,
    stripe_owns_loss_liability,
    stripe_owns_onboarding,
    stripe_owns_pricing,
  } = merchant_data || {};

  return {
    connected_account_id: account_id,
    merchant_id: account_id,
    platform_id,
    livemode,
    country: country ?? 'undefined',
    hasExpressDashboardAccess: has_express_dashboard_access ?? 'undefined',
    hasStandardDashboardAccess: has_standard_dashboard_access ?? 'undefined',
    isMerchant: !!merchant_data,
    isV2AccountSession: is_v2_account_session,
    stripeOwnsLossLiability: stripe_owns_loss_liability ?? 'undefined',
    stripeOwnsOnboarding: stripe_owns_onboarding ?? 'undefined',
    stripeOwnsPricing: stripe_owns_pricing ?? 'undefined',
  };
};
/* eslint-enable camelcase */

export const FILTERED_ACCOUNT_SESSION_CLAIM_FIELDS = [
  'user_session_api_key',
  'user_session_expires_at',
  'user',
] as const;

export const getClaimAccountSessionPromise =
  async (): Promise<FilteredAccountSessionClaim> =>
    claimAccountSessionPromise.promise;

export const claimAccountSession = async (
  iframeRequest: IFrameClaimAccountSessionRequest,
  sendRetrievedUserKeyAnalytics: () => void,
): Promise<
  | IFrameClaimAccountSessionResponse
  | IFrameMessageFailedResponse
  | IFrameMessageFetchErrorResponse
> => {
  const {request, id, type} = iframeRequest;
  try {
    if (request.setNewKey) {
      claimAccountSessionPromise = new DeferredPromise();
    }

    const claimResponse = await claimClientSecret(request);

    // Set the data frame's livemode
    if (claimResponse.livemode !== undefined) {
      setDataLayerFrameLivemode(claimResponse.livemode);
    }

    // All analytics and sentry events after authentication should include these properties
    const authMetadata = extractObservabilityAuthMetadata(claimResponse);
    setObservabilityAuthMetadata(authMetadata);

    if (claimResponse.user_session_api_key) {
      // if we have a user_session_api_key, the refresh token is still valid and may be used on a subsequent session claim
      const userSessionRefreshToken = String(authStore.getUserRefreshToken());
      authStore.persist(
        {
          apiKey: claimResponse.user_session_api_key,
          type: 'stripe_user',
          userSessionRefreshToken,
          user: claimResponse.user || undefined,
        },
        claimResponse.user_session_expires_at
          ? new Date(claimResponse.user_session_expires_at * 1000)
          : undefined,
      );
      sendRetrievedUserKeyAnalytics();
    }

    // The api_key from claim response could be null when we override API key
    // However, this code path will not be hit when we override API key, so we can set it to a fake value (existing backend behaviour)
    authStore.persist({
      apiKey: claimResponse.api_key || 'no_api_key',
      type: 'account_session',
    });

    FILTERED_ACCOUNT_SESSION_CLAIM_FIELDS.forEach((field) => {
      delete claimResponse[field];
    });

    setClaimInfo({
      connectedAccountId: claimResponse.account_id,
      livemode: claimResponse.livemode,
      stripethirdpartyBaseUrl: claimResponse.stripethirdparty_base_url,
      riskSignalsHcaptchaSitekey:
        claimResponse.risk_signals_hcaptcha_sitekey ?? undefined,
    });
    claimAccountSessionPromise.resolve(claimResponse);
    return {
      id,
      type,
      requestType: 'claimAccountSessionResponse',
      response: claimResponse,
    };
  } catch (e: any) {
    // Clear the analytics and sentry metadata
    setObservabilityAuthMetadata({});

    if (e instanceof FetchApiError) {
      const {error} = e;
      if (
        error.httpStatus &&
        error.httpStatus >= 400 &&
        error.httpStatus < 500
      ) {
        devLogger.warn(
          'Error when authenticating in the FrameMessenger',
          error.message,
        );
      }
    }

    claimAccountSessionPromise.reject(e);

    if (e instanceof FetchApiError) {
      return {
        id,
        type,
        requestType: 'fetchErrorResponse',
        error: serializeFetchErrorForPostMessage(e),
      };
    } else {
      return {
        id,
        type,
        requestType: 'errorResponse',
        error: e,
      };
    }
  }
};

export const getValidApiKeyOverrideMetadata = (
  apiKeyOverride: string | undefined,
  platformIdOverride: string | undefined,
) => {
  if (apiKeyOverride && platformIdOverride) {
    return {
      apiKeyOverride,
      platformIdOverride,
    };
  }

  return undefined;
};

async function claimClientSecret({
  clientSecret,
  publishableKey,
  apiHost,
  merchantIdOverride,
  livemodeOverride,
  apiKeyOverride,
  platformIdOverride,
  hostApp,
  enableMcpTokenExchangeMechanism,
  workspaceOverrideForRetrieveEmbeddedFlags,
}: IFrameClaimAccountSessionRequest['request']): Promise<AccountSessionClaimFragmentData> {
  const validApiKeyOverrideMetadata = getValidApiKeyOverrideMetadata(
    apiKeyOverride,
    platformIdOverride,
  );

  if (validApiKeyOverrideMetadata) {
    if (enableMcpTokenExchangeMechanism) {
      return exchangeTokenForMcp({
        apiHost,
        apiKey: validApiKeyOverrideMetadata.apiKeyOverride,
        merchantIdOverride,
        livemodeOverride,
      });
    }

    return retrieveEmbeddedFlags({
      apiHost,
      platformId: validApiKeyOverrideMetadata.platformIdOverride,
      apiKey: validApiKeyOverrideMetadata.apiKeyOverride,
      merchantIdOverride,
      livemodeOverride,
      workspaceOverrideForRetrieveEmbeddedFlags,
    });
  } else {
    return retrieveAccountSessionClaim({
      apiHost,
      publishableKey,
      clientSecret,
      merchantIdOverride,
      livemodeOverride,
      hostApp,
    });
  }
}

async function retrieveAccountSessionClaim({
  apiHost,
  publishableKey,
  clientSecret,
  merchantIdOverride,
  livemodeOverride,
  hostApp,
}: {
  apiHost: string;
  publishableKey: string;
  clientSecret: string;
  merchantIdOverride?: string;
  livemodeOverride?: boolean;
  hostApp?: USE_FOR_ANALYTICS_ONLY_HostApp;
}): Promise<AccountSessionClaimFragmentData> {
  const headers: HeadersInit = {
    'Content-Type': 'application/x-www-form-urlencoded',
  };
  if (merchantIdOverride) {
    headers[ACCOUNT_HEADER] = merchantIdOverride;
  }
  if (livemodeOverride !== undefined) {
    headers[LIVEMODE_HEADER] = livemodeOverride.toString();
  }

  const response = fetch(`https://${apiHost}/v1/account_sessions/claim`, {
    method: 'POST',
    headers,
    body: getFormURLEncodedBody({
      client_secret: clientSecret,
      user_session_refresh_token: authStore.getUserRefreshToken() ?? undefined,
      key: publishableKey,
      _stripe_version: STRIPE_API_VERSION,
      analytics_only_host_app: hostApp,
    }),
  }).then((fetchResponse) => {
    return handleFetchResponse(fetchResponse);
  });

  return response;
}

async function retrieveEmbeddedFlags({
  apiHost,
  platformId,
  apiKey,
  merchantIdOverride,
  livemodeOverride,
  workspaceOverrideForRetrieveEmbeddedFlags,
}: {
  apiHost: string;
  platformId: string;
  apiKey: string;
  merchantIdOverride?: string;
  livemodeOverride?: boolean;
  workspaceOverrideForRetrieveEmbeddedFlags?: string;
}): Promise<AccountSessionClaimFragmentData> {
  const headers: HeadersInit = {
    Authorization: `Bearer ${apiKey}`,
    'Content-Type': 'application/x-www-form-urlencoded',
    [STRIPE_VERSION_HEADER]: `${STRIPE_API_VERSION};`,
  };
  // When both Stripe-Account and Stripe-Context headers are sent, the backend prioritizes
  // Stripe-Account and ignores Stripe-Context. To ensure the context header is used
  // (which supports workspace/org-level identification needed for V2 accounts),
  // we must omit the account header when a workspaceOverrideForRetrieveEmbeddedFlags is provided.
  // See https://trailhead.corp.stripe.com/docs/iam/how-to-use-stripe-headers-for-request-identification-in-orgs-and-workspaces
  if (workspaceOverrideForRetrieveEmbeddedFlags) {
    headers[CONTEXT_HEADER] = workspaceOverrideForRetrieveEmbeddedFlags;
  } else if (merchantIdOverride) {
    headers[ACCOUNT_HEADER] = merchantIdOverride;
  }
  if (livemodeOverride !== undefined) {
    headers[LIVEMODE_HEADER] = livemodeOverride.toString();
  }

  const embeddedFlagsUrl = `https://${apiHost}/v1/retrieve_embedded_flags?platform_id=${encodeURIComponent(
    platformId,
  )}`;

  const retrieveFlagsResponse = fetch(new URL(embeddedFlagsUrl), {
    method: 'GET',
    headers,
  }).then((fetchResponse) => {
    return handleFetchResponse(fetchResponse);
  });

  // Modify the response's hardcoded fields to work with existing embedded
  const retrieveFlagsData = await retrieveFlagsResponse;
  retrieveFlagsData.api_key = apiKey;
  const date = new Date();
  date.setDate(date.getDate() + 365); // A year into the future, which is equivalent to no expiration
  retrieveFlagsData.api_key_expiry = date.getDate() / 1000;

  return retrieveFlagsData;
}

async function exchangeTokenForMcp({
  apiHost,
  apiKey,
  merchantIdOverride,
  livemodeOverride,
}: {
  apiHost: string;
  apiKey: string;
  merchantIdOverride?: string;
  livemodeOverride?: boolean;
}): Promise<AccountSessionClaimFragmentData> {
  const headers: HeadersInit = {
    Authorization: `Bearer ${apiKey}`,
    'Content-Type': 'application/x-www-form-urlencoded',
    [STRIPE_VERSION_HEADER]: `${STRIPE_API_VERSION};`,
  };
  if (merchantIdOverride) {
    headers[ACCOUNT_HEADER] = merchantIdOverride;
  }
  if (livemodeOverride !== undefined) {
    headers[LIVEMODE_HEADER] = livemodeOverride.toString();
  }

  const tokenExchangeUrl = `https://${apiHost}/v1/test_helpers/mcp_erak`;

  const tokenExchangeResponse = fetch(new URL(tokenExchangeUrl), {
    method: 'POST',
    headers,
  }).then((fetchResponse) => {
    return handleFetchResponse(fetchResponse);
  });

  const tokenExchangeData = await tokenExchangeResponse;

  return tokenExchangeData;
}
